import React from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const toNumberOrNull = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const splitResponseList = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return [];
        return trimmed
            .split(/\r?\n/)
            .map((item) => item.trim())
            .filter(Boolean);
    }
    return [];
};

const isErrorQuestion = (question) => {
    const candidates = [
        question?.code,
        question?.questionCode,
        question?.shortCode,
        question?.name,
        question?.label,
    ];
    const numericResponse = (question?.responseType || '').toLowerCase() === 'number';
    return candidates.some((value) => {
        if (typeof value !== 'string') return false;
        const normalized = value.toLowerCase();
        return normalized.includes('error') || normalized.includes('repet');
    }) || numericResponse;
};

const interpretCognitiveState = (finalScore) => {
    if (finalScore <= 15) return 'Probable demencia';
    if (finalScore <= 25) return 'Posible demencia tipo Alzheimer';
    if (finalScore <= 27) return 'Posible demencia (criterio para ancianos)';
    if (finalScore <= 29) return 'Posible demencia (criterio para adultos)';
    return 'Normal';
};

const computeIsaacsResult = (register, scale) => {
    if (!register || !scale) return null;
    const responses = Array.isArray(register?.scaleResponses) ? register.scaleResponses : [];
    const questions = Array.isArray(scale?.questions) ? scale.questions : [];
    if (!responses.length || !questions.length) return null;

    const questionById = new Map(questions.map((q) => [q.id, q]));
    let initialScore = 0;
    let errors = null;

    responses.forEach((response) => {
        const question = questionById.get(response?.scaleQuestionId);
        if (!question) return;

        if (isErrorQuestion(question)) {
            if (errors == null) {
                errors =
                    toNumberOrNull(response?.numericValue) ??
                    toNumberOrNull(response?.score) ??
                    toNumberOrNull(response?.textValue) ??
                    0;
            }
            return;
        }

        const responseType = (question?.responseType || '').toLowerCase();
        if (responseType !== 'text') return;
        const items = splitResponseList(response?.responseList ?? response?.textValue ?? '');
        if (!items.length) return;
        const uniqueCount = new Set(items.map((item) => item.toLowerCase())).size;
        initialScore += Math.min(uniqueCount, 10);
    });

    const errorValue = errors ?? 0;
    const finalScore = Math.max(0, initialScore - errorValue);
    const cognitiveState = interpretCognitiveState(finalScore);
    return {
        initialScore,
        errors: errorValue,
        finalScore,
        cognitiveState,
    };
};

export const ScaleResultISAACS = ({
    title = 'Resultados',
    initialScore,
    totalMaxScore = 40,
    errors = 0,
    finalScore,
    cognitiveState,
    interpretationItems = [
        { label: '<= 15 puntos: Probable demencia' },
        { label: '<= 25 puntos: Posible demencia tipo Alzheimer' },
        { label: '<= 27 puntos: Posible demencia (criterio para ancianos)' },
        { label: '<= 29 puntos: Posible demencia (criterio para adultos)' },
        { label: '> 29 puntos: Normal', muted: true },
    ],
    onComputedResult,
    register,
    scale,
}) => {
    const derived = React.useMemo(() => computeIsaacsResult(register, scale), [register, scale]);
    React.useEffect(() => {
            // Usamos onComputedResult, que es lo que recibimos por props
            if (derived && typeof onComputedResult === 'function') {
                onComputedResult(derived); 
            }
        }, [derived, onComputedResult]);
    const displayInitialScore = derived?.initialScore ?? toNumberOrNull(initialScore) ?? 0;
    const displayErrors = derived?.errors ?? toNumberOrNull(errors) ?? 0;
    const displayFinalScore =
        derived?.finalScore ??
        toNumberOrNull(finalScore) ??
        Math.max(0, displayInitialScore - displayErrors);
    const displayState = derived?.cognitiveState || cognitiveState;

    return (
        <Paper elevation={0} style={{ padding: 24, borderRadius: 12 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 12, color: '#0d1b2a' }}>
                {title}
            </Typography>

            <List style={{ paddingTop: 0, marginBottom: 16 }}>
                <ListItem disableGutters>
                    <ListItemIcon style={{ minWidth: 24 }}>
                        <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                    </ListItemIcon>
                    <ListItemText
                        primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                        primary={`Puntuacion inicial: ${displayInitialScore}/${totalMaxScore}`}
                    />
                </ListItem>

                <ListItem disableGutters>
                    <ListItemIcon style={{ minWidth: 24 }}>
                        <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                    </ListItemIcon>
                    <ListItemText
                        primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                        primary={`Errores y repeticiones: ${displayErrors}`}
                    />
                </ListItem>

                <ListItem disableGutters>
                    <ListItemIcon style={{ minWidth: 24 }}>
                        <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                    </ListItemIcon>
                    <ListItemText
                        primaryTypographyProps={{ style: { color: '#0d1b2a', fontWeight: 600 } }}
                        primary={`Puntuacion Final: ${displayFinalScore}/${totalMaxScore}`}
                    />
                </ListItem>

                {displayState && (
                    <ListItem disableGutters>
                        <ListItemIcon style={{ minWidth: 24 }}>
                            <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                            primary={`Estado Cognitivo: ${displayState}`}
                        />
                    </ListItem>
                )}
            </List>

            <Box mb={1} ml={1}>
                <Typography variant="body2" style={{ fontStyle: 'italic', color: '#0d1b2a' }}>
                    Interpretacion:
                </Typography>
            </Box>

            <List style={{ paddingTop: 0 }}>
                {interpretationItems.map((item, index) => (
                    <ListItem key={item?.label ?? index} disableGutters>
                        <ListItemIcon style={{ minWidth: 24 }}>
                            <FiberManualRecordIcon
                                fontSize="small"
                                style={{ color: item?.muted ? '#9e9e9e' : '#0d1b2a' }}
                            />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{
                                style: {
                                    color: item?.muted ? '#9e9e9e' : '#0d1b2a',
                                },
                            }}
                            primary={item?.label || ''}
                        />
                    </ListItem>
                ))}
            </List>
        </Paper>
    );
};

export default ScaleResultISAACS;
